import React from 'react';
import { useContentSlot, renderRichContent } from '../lib/content-slots-client';
import type { ContentType } from '../lib/content-slots-client';
import { useEditMode } from '../contexts/EditModeContext';

interface ContentSlotProps {
  slotId: string;
  fallback: string;
  contentType: ContentType;
  pageSection?: string;
  label?: string;
  as?: React.ElementType;
  className?: string;
}

export default function ContentSlot({
  slotId,
  fallback,
  contentType,
  pageSection = '',
  label = '',
  as: Element = 'span',
  className = '',
}: ContentSlotProps) {
  const { slot } = useContentSlot(slotId);
  const editMode = useEditMode();

  const displayContent = slot?.content ?? fallback;
  const isRich = contentType === 'paragraph';

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    editMode.onEditContent({
      slotId,
      currentContent: displayContent,
      fallback,
      contentType,
      pageSection: slot?.page_section ?? pageSection,
      label: slot?.label || label || slotId,
    });
  };

  // ── Public mode ──────────────────────────────────────────────────────────────
  if (!editMode.active) {
    if (isRich) {
      return (
        <Element
          className={className}
          dangerouslySetInnerHTML={{ __html: renderRichContent(displayContent) }}
        />
      );
    }
    return <Element className={className}>{displayContent}</Element>;
  }

  // ── Edit mode ────────────────────────────────────────────────────────────────
  const inner = isRich
    ? (
      <span
        className="group/cs relative cursor-text rounded-sm hover:outline hover:outline-1 hover:outline-offset-2 hover:outline-[#39FF14]/50 transition-all"
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={e => e.key === 'Enter' && handleClick(e)}
        dangerouslySetInnerHTML={{ __html: renderRichContent(displayContent) }}
      />
    )
    : (
      <span
        className="group/cs relative cursor-text rounded-sm hover:outline hover:outline-1 hover:outline-offset-2 hover:outline-[#39FF14]/50 transition-all"
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={e => e.key === 'Enter' && handleClick(e)}
      >
        {displayContent}
        {/* Edit pill — appears on hover */}
        <span className="absolute -top-1 -right-1 opacity-0 group-hover/cs:opacity-100 transition-opacity pointer-events-none z-[30] bg-[#39FF14] text-[#0D0D0D] text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">
          ✎ edit
        </span>
      </span>
    );

  return <Element className={className}>{inner}</Element>;
}
